import './_forgot-password.style.scss';

import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Modal, notification } from 'antd';
import { useFormik } from 'formik';
import React, { useState } from 'react';

import colors from '../../../foundation/assets/colors';
import LogoDark from '../../../foundation/assets/svgs/LogoDark';
import Input from '../../../foundation/components/input/Input';
import { useAppDispatch } from '../../../store/hooks';
import { requestPasswordReset } from '../redux/async_thunks';
import { ForgotPasswordValidationSchema } from './form_validation';

type ForgotPasswordModalProps = {
  onClose: () => void;
};

const ForgotPasswordModal = ({ onClose }: ForgotPasswordModalProps) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: ForgotPasswordValidationSchema,
    onSubmit: async (values) => {
      if (isLoading) {
        return;
      }

      setIsLoading(true);
      try {
        await dispatch(requestPasswordReset({ email: values.email })).unwrap();
        setEmailSent(true);
      } catch (error: any) {
        // Display allauth errors exactly as they come from the backend
        const allauthErrors: Array<{ message: string }> = error?.errors ?? [];
        notification.error({
          message: 'Request failed',
          description:
            allauthErrors.map((e) => e.message).join(' ') ||
            error?.message ||
            'Failed to send password reset email.',
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <Modal
      open
      closable={false}
      footer={null}
      title={null}
      maskClosable={false}
      onCancel={onClose}
      width={430}
      centered
      zIndex={3000}
      className="l-forgot-password"
      wrapClassName="l-forgot-password__modal-wrapper"
      rootClassName="l-forgot-password__modal-root"
    >
      <div className="l-forgot-password__header">
        <div className="l-forgot-password__header-title">
          <div>
            <LogoDark />
          </div>
          <div
            className="l-forgot-password__header-close-icon"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faTimes} color={colors.primary} />
          </div>
        </div>
        <div>Reset your password</div>
      </div>
      <div className="l-forgot-password__body">
        {emailSent ? (
          <Alert
            type="success"
            showIcon
            message="Check your email"
            description="If an account exists with that email address, you will receive a password reset link shortly."
            className="l-forgot-password__form-fields"
          />
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <div className="l-forgot-password__form-fields">
              <div className="l-forgot-password__field-wrapper">
                <Input
                  placeholder="Enter your email address"
                  name="email"
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email ? formik.errors.email : undefined}
                />
              </div>
            </div>
            <div className="l-forgot-password__form-actions">
              <Button
                className="l-forgot-password__submit-btn"
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                Send Reset Link
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default ForgotPasswordModal;
